import { useEffect, useState } from 'react';
import { filterOptions, getExpectedFormatMessage, getMsg, getItemCount } from './AdvancedSearchHelpers';
import Loader from '@/components/Common/Loader';
import { useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { trackEnrichmentAction } from '../../utils/analytics';
import { handleDownloadAdvancedSearch } from './helpers/handleDownloadAdvancedSearch';
import SharedButton from '@/global/Button/Button';
import './advanced-search.css';
import { ArrowDownTrayIcon, ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import SharedBadge from '@/global/Badge/Badge';
import TabSelector from '@/global/TabSelector/TabSelector';
import BackgroundPages from '@/global/Background/BackgroundPages';
import RestrictedAccess from '../Modal/RestrictedAccess';
import { userAppContext } from '@/context/Context';
import { UserContext } from '@/types';
import { trackEvent } from '@/utils/analytics';
import { advancedExamples } from './AdvancedExamples';

const tabs = ['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'];

interface AdvancedSearchProps {
  searchType: string;
  setSearchType: (type: string) => void;
  selectedFilter: string;
  setSelectedFilter: (filter: string) => void;
  text: string;
  handleQueryChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSearchClick: () => void;
  clearInputText: () => void;
  showExamples: () => void;
  renderSuggestions: () => React.ReactElement;
  loading: boolean;
  suggestionsData: any[];
  handleBackClick: () => void;
  handleSearchSelectedItems: () => void;
  handleSelectAll: () => void;
  handleClearAll: () => void;
  showTextarea: boolean;
  pendingAutoSearch: boolean;
  autoSearchTriggeredFromEnrichment: boolean;
  validateInput: (input: any, searchType: string) => boolean;
  selectedItems: any[];
}

const AdvancedSearch = ({
  searchType,
  setSearchType,
  selectedFilter,
  setSelectedFilter,
  text,
  handleQueryChange,
  handleSearchClick,
  clearInputText,
  showExamples,
  renderSuggestions,
  loading,
  suggestionsData,
  handleBackClick,
  handleSearchSelectedItems,
  handleSelectAll,
  handleClearAll,
  showTextarea,
  pendingAutoSearch,
  autoSearchTriggeredFromEnrichment,
  validateInput,
  selectedItems,
}: AdvancedSearchProps) => {
  const navigate = useNavigate();
  const { setEnrichmentFrom, pendingAdvancedSearchRedirect, clearPendingAdvancedSearchRedirect } =
    useEnrichmentState();
  const { isAuthenticated } = userAppContext() as unknown as UserContext;

  const [showExamplesButton, setShowExamplesButton] = useState<boolean>(true);
  const [replaceFeedback, setReplaceFeedback] = useState<string>('');

  const showExamplesOnClick = () => {
    showExamples();
    setShowExamplesButton(false);
  };

  const clearListOnClick = () => {
    clearInputText();
    setShowExamplesButton(true);
  };

  useEffect(() => {
    if (showTextarea && !text.trim()) {
      setShowExamplesButton(true);
    }
  }, [showTextarea, text]);

  useEffect(() => {
    const availableFilters = filterOptions[searchType] || [];

    if (availableFilters.length > 0) {
      if (!availableFilters.find(filter => filter.value === selectedFilter)) {
        setSelectedFilter(availableFilters[0].value);
      }
    }
  }, [searchType, setSelectedFilter, selectedFilter]);

  const handleEnrichmentRedirect = () => {
    trackEnrichmentAction('enrichment_analysis_click', 'advanced_search');

    const enrichmentFrom = {
      from: 'advancedSearch',
      searchType,
      selectedFilter,
      text,
    };

    setEnrichmentFrom(enrichmentFrom);

    navigate('/enrichment', { state: enrichmentFrom });
  };

  const handleEnrichmentWithSelectedItems = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item for enrichment analysis.');
      return;
    }

    trackEnrichmentAction('enrichment_analysis_click', 'advanced_search_selected_items');

    // For gene names, send symbols to enrichment
    let displayText = '';
    let enrichmentFilterType = selectedFilter;

    if (searchType === 'GENES' && selectedFilter === 'name') {
      displayText = selectedItems
        .map((item: any) => {
          if (item) {
            const symbol = item.normalizedName ? item.normalizedName : item.symbol;
            return symbol;
          }
        })
        .filter(symbol => symbol)
        .join('\n');

      enrichmentFilterType = 'geneSymbol';
    } else {
      const selectedIdentifiers = selectedItems
        .map((item: any) => {
          if (!item) return '';
          switch (searchType) {
            case 'GENES':
              return item.symbol || item.geneID || item.normalizedId || '';
            case 'VARIANTS':
              return item.strID || '';
            default:
              return '';
          }
        })
        .filter(id => id)
        .join('\n');

      displayText = selectedIdentifiers;
    }

    const enrichmentFrom = {
      from: 'advancedSearchSelectedItems',
      searchType,
      selectedFilter: enrichmentFilterType,
      text: displayText,
    };

    setEnrichmentFrom(enrichmentFrom);

    navigate('/enrichment', { state: enrichmentFrom });
  };

  const filteredNotFoundItems = suggestionsData.filter((item: any) => {
    return item.validationError || !validateInput(item.query, searchType);
  });

  const uniqueResults = new Set();
  const uniqueQueries = new Set();

  suggestionsData.forEach((item: any) => {
    uniqueQueries.add(item.query);

    if (!item.validationError && item.found && Array.isArray(item.results)) {
      item.results.forEach((result: any) => {
        uniqueResults.add(JSON.stringify(result));
      });
    }
  });

  const totalSearched = uniqueQueries.size;
  const totalFound = uniqueResults.size;
  const isGeneNameSearch = searchType === 'GENES' && selectedFilter === 'name';
  const hasPendingEnrichmentReplacement = Boolean(pendingAdvancedSearchRedirect?.originalRequestText);

  const handleReplaceOfficialSymbolsInOriginalRequest = () => {
    if (!pendingAdvancedSearchRedirect?.originalRequestText) {
      setReplaceFeedback('Original enrichment request not found.');
      return;
    }

    const unresolvedItems = pendingAdvancedSearchRedirect.unresolvedItems || [];
    const unresolvedSet = new Set(unresolvedItems.map(item => item.trim().toUpperCase()));
    const replacementMap = new Map<string, string>();

    suggestionsData.forEach((item: any) => {
      const queryKey = (item?.query || '').trim().toUpperCase();
      if (
        !queryKey ||
        !unresolvedSet.has(queryKey) ||
        !Array.isArray(item.results) ||
        item.results.length === 0
      ) {
        return;
      }

      const firstMatch = item.results[0];
      const officialSymbol = firstMatch?.normalizedName || firstMatch?.symbol;
      if (officialSymbol) {
        replacementMap.set(queryKey, String(officialSymbol).trim());
      }
    });

    const replacedLines = pendingAdvancedSearchRedirect.originalRequestText.split('\n').map(line => {
      const trimmed = line.trim();
      const key = trimmed.toUpperCase();
      return replacementMap.get(key) || trimmed;
    });

    const resolvedText = replacedLines.filter(Boolean).join('\n');

    if (!resolvedText.trim()) {
      setReplaceFeedback('No symbols could be replaced.');
      return;
    }

    clearPendingAdvancedSearchRedirect();
    navigate('/enrichment', {
      state: {
        searchType: 'GENES',
        selectedFilter: 'geneSymbol',
        text: resolvedText,
      },
    });
  };

  const examplePreview = (
    (advancedExamples as Record<string, Record<string, string[]>>)[searchType]?.[selectedFilter] || []
  )
    .slice(0, 3)
    .join(' · ');
  const itemCount = getItemCount(text);

  return (
    <div className="apply-trial-page">
      <BackgroundPages />
      <div className="kb-page kb-page--fit">
        <SharedBadge
          text="Advanced Search Tool"
          icon={<MagnifyingGlassIcon className="h-4 w-4 mr-1" />}
          variant="secondary"
          size="sm"
        />
        <h1>Search the Knowledge Base</h1>
        <p className="kb-lead">
          Paste a list of entities, one per line, and match them against curated disease–gene–variant
          associations.
        </p>
        <RestrictedAccess isRestricted={!isAuthenticated}>
          <div className="kb-card">
            <div className="kb-card-head">
              <h2>Query builder</h2>
              <div data-demo-tour="advanced-search-tabs">
                <TabSelector
                  options={tabs.map(type => ({ label: type.toUpperCase(), value: type }))}
                  variant="primary"
                  size="sm"
                  value={searchType}
                  onChange={setSearchType}
                  justify="start"
                />
              </div>
            </div>
            {showTextarea ? (
              <div className="kb-grid">
                <div>
                  <label className="kb-field-label" htmlFor="advanced-search-match-by">
                    Match by
                  </label>
                  <select
                    id="advanced-search-match-by"
                    value={selectedFilter}
                    onChange={e => setSelectedFilter(e.target.value)}
                    className="kb-select"
                    data-demo-tour="advanced-search-filter-select"
                  >
                    {filterOptions[searchType].map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {showExamplesButton && itemCount === 0 && (
                    <button
                      type="button"
                      id="advanced_search_show_examples"
                      data-demo-tour="advanced-search-show-examples"
                      className="kb-examples"
                      onClick={showExamplesOnClick}
                    >
                      <strong>Show examples</strong>
                      <p>
                        {examplePreview ||
                          'Add a few entities to see how matching works against the knowledge base.'}
                      </p>
                    </button>
                  )}
                  {((searchType === 'VARIANTS' && selectedFilter === 'strID') ||
                    (searchType === 'GENES' && selectedFilter !== 'name')) && (
                    <div className="kb-side-actions">
                      <SharedButton
                        id="advanced_search_enrichment_analysis"
                        data-demo-tour="advanced-search-enrichment-analysis"
                        variant="secondary"
                        onClick={handleEnrichmentRedirect}
                        className="filter-upload"
                        title="Go to Enrichment Analysis"
                      >
                        Enrichment Analysis
                      </SharedButton>
                    </div>
                  )}
                </div>
                <div>
                  {autoSearchTriggeredFromEnrichment && (
                    <div className="mb-3 p-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-900">
                      <div className="flex items-center gap-2">
                        <Loader />
                        <p className="text-sm font-medium">
                          We are searching these items automatically from Enrichment. Please wait...
                        </p>
                      </div>
                    </div>
                  )}
                  <label className="kb-field-label" htmlFor="advanced-search-query-list">
                    Query list
                  </label>
                  <textarea
                    id="advanced-search-query-list"
                    placeholder="one per line"
                    value={text}
                    onChange={handleQueryChange}
                    className="kb-textarea"
                    rows={8}
                    data-demo-tour="advanced-search-input-textarea"
                  />
                  {(pendingAutoSearch || loading) && itemCount > 0 && (
                    <p className="text-sm text-blue-700 mt-2 italic">
                      Searching your list automatically, please wait...
                    </p>
                  )}
                  <div className="kb-grid-foot">
                    <span className="kb-item-count">{itemCount} items</span>
                    <div className="kb-grid-actions">
                      {itemCount > 0 && (
                        <SharedButton
                          id="advanced_search_clear_list"
                          variant="secondary"
                          onClick={clearListOnClick}
                          className="filter-upload"
                        >
                          Clear List
                        </SharedButton>
                      )}
                      <SharedButton
                        id="advanced_search_search_list"
                        data-demo-tour="advanced-search-search-list"
                        variant="gradient"
                        onClick={() => {
                          trackEvent({
                            name: 'search_submit',
                            buttonId: 'advanced_search_search_list',
                            label: 'Search list',
                            page: 'advanced_search',
                            section: 'search_form',
                            feature: 'search',
                            eventGroup: 'search',
                            eventCategory: 'search',
                          });
                          handleSearchClick();
                        }}
                        className="filter-upload"
                        disabled={itemCount === 0}
                      >
                        {loading ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Loader />
                            Searching...
                          </span>
                        ) : (
                          <>
                            Run search
                            <ArrowRightIcon className="h-4 w-4 ml-1" />
                          </>
                        )}
                      </SharedButton>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="kb-card-body">
                {renderSuggestions()}

                <div className="error-message">
                  {!loading && suggestionsData.length === 0 && (
                    <p style={{ color: 'red', fontStyle: 'italic' }}>
                      No data found or the service is temporarily unavailable.
                    </p>
                  )}

                  {filteredNotFoundItems.length > 0 && (
                    <div>
                      <p style={{ color: 'red', fontStyle: 'italic' }}>
                        Input search does not follow the expected format for {searchType}{' '}
                        {getMsg(searchType, selectedFilter)}:
                      </p>
                      <ul style={{ color: 'red', fontStyle: 'italic' }}>
                        {Array.from(
                          new Map(filteredNotFoundItems.map((item: any) => [item.query, item])).values(),
                        ).map((uniqueItem: any, index: number) => (
                          <li key={index}>{uniqueItem.query}</li>
                        ))}
                      </ul>
                      Examples of {searchType} {getExpectedFormatMessage(searchType, selectedFilter)}
                    </div>
                  )}
                </div>
                <p>
                  From the <strong>{totalSearched}</strong> unique input items, we found{' '}
                  <strong>{totalFound}</strong> in DISGENET.
                </p>
                </div>

                <div className="kb-card-actions">
                <SharedButton
                  id="advanced_search_back"
                  data-demo-tour="advanced-search-back"
                  variant="secondary"
                  size="sm"
                  onClick={handleBackClick}
                  className="filter-upload"
                >
                  Back
                </SharedButton>
                {selectedItems?.length !==
                  (suggestionsData?.flatMap((item: any) => item.results) || []).length && (
                  <SharedButton
                    id="advanced_search_select_all"
                    data-demo-tour="advanced-search-select-all"
                    variant="gradient"
                    size="sm"
                    onClick={handleSelectAll}
                    className="filter-upload"
                  >
                    Select All
                  </SharedButton>
                )}

                {selectedItems?.length > 0 && (
                  <SharedButton
                    id="advanced_search_clear_selection"
                    variant="secondary"
                    size="sm"
                    onClick={handleClearAll}
                    className="filter-upload"
                  >
                    Clear Selection
                  </SharedButton>
                )}
                {selectedItems?.length > 0 && (
                  <SharedButton
                    id="advanced_search_search_selected"
                    data-demo-tour="advanced-search-search-selected"
                    variant="gradient"
                    size="sm"
                    onClick={() => {
                      trackEvent({
                        name: 'search_submit',
                        buttonId: 'advanced_search_search_selected_items',
                        label: 'Search selected items',
                        page: 'advanced_search',
                        section: 'search_results',
                        feature: 'search',
                        eventGroup: 'search',
                        eventCategory: 'search',
                      });
                      handleSearchSelectedItems();
                    }}
                    className="filter-upload"
                  >
                    Search Selected Items
                  </SharedButton>
                )}
                {selectedItems?.length > 0 && (searchType === 'GENES' || searchType === 'VARIANTS') && (
                  <SharedButton
                    id="advanced_search_enrichment"
                    variant="secondary"
                    size="sm"
                    onClick={handleEnrichmentWithSelectedItems}
                    className="filter-upload"
                  >
                    Enrichment Analysis
                  </SharedButton>
                )}
                {!loading && totalFound > 0 && (
                  <>
                    {isGeneNameSearch && hasPendingEnrichmentReplacement && (
                      <SharedButton
                        id="advanced_search_replace_official_symbols"
                        variant="secondaryOutline"
                        size="sm"
                        onClick={handleReplaceOfficialSymbolsInOriginalRequest}
                        className="filter-upload"
                      >
                        Replace official symbols in original request
                      </SharedButton>
                    )}
                    {replaceFeedback && <p className="text-sm text-blue-700 mt-1">{replaceFeedback}</p>}
                  </>
                )}
                {!loading && totalFound > 0 && (
                  <SharedButton
                    id="advanced_search_download"
                    variant="secondaryOutline"
                    size="sm"
                    onClick={() =>
                      handleDownloadAdvancedSearch(
                        suggestionsData,
                        searchType,
                        selectedFilter,
                        text,
                        totalSearched,
                      )
                    }
                    className="filter-upload"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download Results (ZIP)
                  </SharedButton>
                )}
                </div>
              </>
            )}
          </div>
        </RestrictedAccess>
      </div>
    </div>
  );
};

export default AdvancedSearch;
